import { atom, useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useUserState } from '~/atoms/UserState'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  useDeleteVoicemail,
  useGetAllExtensions,
  useGetVoicemail,
  useGetVoicemailExtensions,
  useGetVoicemails,
  useMarkVoicemailRead,
  useMarkVoicemailUnread,
  useMoveVoicemail,
} from '~/queries/voicemailQueries'
import { VoicemailEmitterEventType } from '~/services/EventEmitter/types/VoicemailEmitterTypes'
import Logger from '~/services/Logger'
import { UnreadVoicemailStatusFilter } from '~/types/ApiRequestTypes'
import { SearchValue } from '~/types/Shared'
import { getUriComponents } from '~/utils/routing/uriUtils'

import {
  ApiError,
  Extension,
  Voicemail,
  VoicemailInstance,
} from '@mangovoice/types'

const VOICEMAIL_EXTENSIONS_CACHE_STALE_TIME = 1000 * 60 * 1
const DEBUG_STATE_UPDATE_LOGS_ENABLED = true

interface VoicemailListState {
  voicemails?: Voicemail[]
  hasNextPage?: boolean
  nextPageOffset?: number
}

export interface VoicemailViewModel {
  voicemailListState?: VoicemailListState
  voicemailListLoading?: boolean
  voicemailListFetching?: boolean
  voicemailPageLoading?: boolean
  voicemailPageFetching?: boolean
  voicemailExtensionsState?: Extension[]
  unreadVoicemailsCount?: number
  selectedVoicemailId?: number
  selectedVoicemail?: VoicemailInstance
  selectedVoicemailLoading?: boolean
  selectedVoicemailFetching?: boolean
  getSelectedVoicemailError?: ApiError
  selectedVoicemailNotFound?: boolean
  selectedExtensionId?: number | string
  selectedVoicemailBoxName?: string
  unreadFilter?: UnreadVoicemailStatusFilter
  searchValue?: SearchValue
  selectedVoicemailTimestamp?: number
  allExtensionsState?: Extension[]
}

const logger = new Logger('VoicemailViewModel')
let rootVoicemailViewModelState: VoicemailViewModel = {
  searchValue: SearchValue.empty,
}
const voicemailViewModelAtom = atom<VoicemailViewModel>(
  rootVoicemailViewModelState,
)
export const useVoicemailViewModel = () => {
  const { pbxId } = useUserState()
  const { voicemailId } = useParams()
  const [voicemailViewModel, setVoicemailViewModel] = useAtom(
    voicemailViewModelAtom,
  )
  const [allExtensionsState, setAllExtensionsState] = useState<Extension[]>([])

  const { emit } = useEmitterService()

  const getValidatedState = (newViewModelState: VoicemailViewModel) => {
    //ids are changing - clear away error states
    if (
      newViewModelState.selectedVoicemailId !==
      rootVoicemailViewModelState.selectedVoicemailId
    ) {
      newViewModelState.getSelectedVoicemailError = undefined
      newViewModelState.selectedVoicemailNotFound = false
    }

    //not found means none of this other state is valid
    if (newViewModelState.selectedVoicemailNotFound) {
      newViewModelState.selectedVoicemail = undefined
      newViewModelState.selectedVoicemailBoxName = undefined
      newViewModelState.selectedVoicemailTimestamp = undefined
    }

    //if the selected voicemail id has a different value than the selected voicemail, we're about to load a new voicemail, clear everything related to loading
    if (
      newViewModelState.selectedVoicemail &&
      newViewModelState.selectedVoicemail.id !==
        newViewModelState.selectedVoicemailId
    ) {
      newViewModelState.selectedVoicemail = undefined
      newViewModelState.getSelectedVoicemailError = undefined
      newViewModelState.selectedVoicemailNotFound = false
    }

    if (newViewModelState.selectedVoicemail) {
      newViewModelState.getSelectedVoicemailError = undefined
      newViewModelState.selectedVoicemailNotFound = false
    }

    //handle counts on unread voicemails
    newViewModelState.unreadVoicemailsCount =
      newViewModelState.voicemailExtensionsState?.reduce((acc, curr) => {
        return acc + (curr.voicemail_status?.unread_count ?? 0)
      }, 0)

    if (
      newViewModelState.selectedVoicemail &&
      newViewModelState.voicemailExtensionsState
    ) {
      newViewModelState.selectedVoicemailBoxName =
        newViewModelState.voicemailExtensionsState.find(
          (extension) =>
            extension.id === newViewModelState.selectedVoicemail?.extension_id,
        )?.name
    }
    return newViewModelState
  }

  useEffect(() => {
    if (voicemailId) {
      const validatedState = getValidatedState({
        ...voicemailViewModel,
        selectedVoicemailId: Number(voicemailId),
      })
      rootVoicemailViewModelState = validatedState
      setVoicemailViewModel(validatedState)
    }
    if (!voicemailId) {
      const validatedState = getValidatedState({
        ...voicemailViewModel,
        selectedVoicemailId: undefined,
        selectedVoicemail: undefined,
        selectedVoicemailLoading: false,
        selectedVoicemailFetching: false,
        getSelectedVoicemailError: undefined,
        selectedVoicemailNotFound: false,
      })
      rootVoicemailViewModelState = validatedState
      setVoicemailViewModel(validatedState)
    }
  }, [voicemailId])
  const mergeVoicemailListStateData = (newData: Voicemail[]) => {
    const existingData =
      rootVoicemailViewModelState.voicemailListState?.voicemails ?? []
    for (const voicemail of newData) {
      const existingVoicemailIndex = existingData.findIndex(
        (existingVoicemail) => existingVoicemail.id === voicemail.id,
      )
      if (existingVoicemailIndex > -1) {
        existingData[existingVoicemailIndex] = voicemail
      } else {
        existingData.push(voicemail)
      }
    }

    //sort by id descending
    existingData.sort((a, b) => b.id - a.id)
    return existingData
  }

  const { mutateAsync: markVoicemailUnreadAsync } = useMarkVoicemailUnread({
    onRequestCompleted: ({ voicemailId }) => {
      emit(VoicemailEmitterEventType.VOICEMAIL_MARKED_UNREAD, { voicemailId })
    },
  })

  // Use mutateAsync for moveVoicemail
  const { mutateAsync: moveVoicemailAsync } = useMoveVoicemail()

  const updateVoicemailExtensionId = (
    voicemailId: number,
    newExtensionId: number,
  ) => {
    // Find the voicemail in the current voicemailListState
    const voicemail =
      rootVoicemailViewModelState.voicemailListState?.voicemails?.find(
        (v) => v.id === voicemailId,
      )

    if (voicemail) {
      // Store the old extension ID before updating
      const oldExtensionId = voicemail.extension_id

      voicemail.extension_id = newExtensionId

      // Determine the current selected extension
      const currentExtensionId = rootVoicemailViewModelState.selectedExtensionId

      const isAllVoicemailsView =
        currentExtensionId == null ||
        currentExtensionId === '' ||
        currentExtensionId === 0

      // Only remove the voicemail if it's no longer in the selected extension and we're not in "All voicemails"
      if (
        !isAllVoicemailsView &&
        currentExtensionId === oldExtensionId &&
        currentExtensionId !== newExtensionId
      ) {
        // Voicemail no longer matches the filter, remove it from the list
        mutators.voicemailListState = {
          ...rootVoicemailViewModelState.voicemailListState,
          voicemails:
            rootVoicemailViewModelState.voicemailListState?.voicemails?.filter(
              (voicemail) => voicemail.id !== voicemailId,
            ),
        }

        // Deselect if it's the selected voicemail
        if (rootVoicemailViewModelState.selectedVoicemailId === voicemailId) {
          mutators.selectedVoicemailId = undefined
          mutators.selectedVoicemail = undefined
        }
      } else {
        // Voicemail still matches the filter or we're in "All voicemails" view
        // Update state to reflect the change
        mutators.voicemailViewModelState = {
          ...rootVoicemailViewModelState,
          voicemailListState: {
            ...rootVoicemailViewModelState.voicemailListState,
            voicemails: [
              ...(rootVoicemailViewModelState.voicemailListState?.voicemails ??
                []),
            ],
          },
        }
      }
    }
  }

  const mutators = {
    /**
     * @param voicemailId - The ID of the voicemail to set as selected.
     * @description This method will set the selectedVoicemailId and selectedVoicemail properties of the voicemailViewModel.  If the selectedVoicemailId is different than the ID of the currently selected voicemail, the selectedVoicemail property will be set to undefined.
     */
    set selectedVoicemailId(voicemailId: number | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        selectedVoicemailId: voicemailId,
      }
    },
    /**
     * @param voicemail - The voicemail to set as selected.
     * @description When set, clears getSelectedVoicemailError and selectedVoicemailNotFound.
     */
    set selectedVoicemail(voicemail: VoicemailInstance | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        selectedVoicemail: voicemail,
      }
    },
    /**
     * @param error - The error returned from the API Call.
     *
     * @description
     * selectedVoicemail is always set to undefined when this property is set. If the error is a 404, we set the selectedVoicemailNotFound flag to true.
     */
    set selectedVoicemailError(error: ApiError | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        getSelectedVoicemailError: error,
      }
    },
    /**
     * @param notFound - Whether or not the voicemail was found.
     * @description When set, clears selectedVoicemail.
     */
    set voicemailNotFound(notFound: boolean | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        selectedVoicemailNotFound: notFound,
      }
    },
    set selectedExtensionId(extensionId: number | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        selectedExtensionId: extensionId,
      }
    },
    set selectedVoicemailBoxName(voicemailBoxName: string | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        selectedVoicemailBoxName: voicemailBoxName,
      }
    },
    set unreadFilter(unreadFilter: UnreadVoicemailStatusFilter | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        unreadFilter: unreadFilter,
      }
    },
    set selectedVoicemailTimestamp(timestamp: number | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        selectedVoicemailTimestamp: timestamp,
      }
    },
    set voicemailListState(listState: VoicemailListState | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        voicemailListState: listState,
      }
    },
    set voicemailExtensionsState(extensions: Extension[] | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        voicemailExtensionsState: extensions,
      }
    },
    set unreadVoicemailCount(count: number | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        unreadVoicemailsCount: count,
      }
    },
    set searchValue(searchValue: SearchValue | undefined) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        searchValue: searchValue ?? SearchValue.empty,
      }
    },
    set voicemailViewModelState(newViewModelState: VoicemailViewModel) {
      const validatedState = getValidatedState(newViewModelState)

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (DEBUG_STATE_UPDATE_LOGS_ENABLED) {
        logger.debug('VoicemailViewModel State Updated', {
          newViewModelState,
          validatedState,
        })
      }

      rootVoicemailViewModelState = validatedState
      setVoicemailViewModel(validatedState)
    },
  }

  const updateLocalUnreadStatus = (
    voicemailId: number,
    read: boolean,
    extensionId?: number,
  ) => {
    const voicemail =
      rootVoicemailViewModelState.voicemailListState?.voicemails?.find(
        (v) => v.id === voicemailId,
      )

    let stateUpdated = false

    if (voicemail) {
      voicemail.read_at = read ? new Date().toISOString() : null
      stateUpdated = true
    }

    const targetExtensionId = extensionId || voicemail?.extension_id

    const extension =
      rootVoicemailViewModelState.voicemailExtensionsState?.find(
        (e) => e.id === targetExtensionId,
      )

    if (extension?.voicemail_status) {
      const currentUnreadCount = extension.voicemail_status.unread_count ?? 0
      const delta = read ? -1 : 1
      const newUnreadCount = Math.max(0, currentUnreadCount + delta)
      extension.voicemail_status.unread_count = newUnreadCount
      stateUpdated = true
    }

    if (stateUpdated) {
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        voicemailListState: {
          ...rootVoicemailViewModelState.voicemailListState,
          voicemails: [
            ...(rootVoicemailViewModelState.voicemailListState?.voicemails ??
              []),
          ],
        },
        voicemailExtensionsState: [
          ...(rootVoicemailViewModelState.voicemailExtensionsState ?? []),
        ],
      }
    }
  }

  //#region Actions
  const {
    isLoading: selectedVoicemailLoading,
    isFetching: selectedVoicemailFetching,
  } = useGetVoicemail({
    voicemailId: rootVoicemailViewModelState.selectedVoicemailId ?? 0,
    options: {
      refetchOnWindowFocus: false,
    },
    onRequestCompleted: (data) => {
      mutators.selectedVoicemail = data
    },
    onRequestErrored: (error) => {
      mutators.selectedVoicemailError = error
    },
  })

  const { refetch: getVoicemailsInternal } = useGetVoicemails({
    pbxId: pbxId ?? 0,
    extId: rootVoicemailViewModelState.selectedExtensionId ?? '',
    search: rootVoicemailViewModelState.searchValue?.value,
    unread:
      rootVoicemailViewModelState.unreadFilter ??
      UnreadVoicemailStatusFilter.ALL,
    onRequestCompleted: (data) => {
      //optimistically add new entries to the bottom of the list
      mutators.voicemailViewModelState = {
        ...rootVoicemailViewModelState,
        voicemailListState: {
          voicemails: mergeVoicemailListStateData(data.results),
          hasNextPage: data.next !== null,
          nextPageOffset: Number(
            getUriComponents(data.next ?? '').query.params?.offset ?? 0,
          ),
        },
        voicemailListLoading: false,
        voicemailListFetching: false,
      }
    },
    options: {
      refetchOnWindowFocus: false,
      enabled: false,
    },
  })

  const fetchVoicemailsFromOrigin = () => {
    mutators.voicemailListState = {
      voicemails: [],
      hasNextPage: false,
      nextPageOffset: 0,
    }

    mutators.voicemailViewModelState = {
      ...rootVoicemailViewModelState,
      voicemailListLoading: true,
    }
    getVoicemailsInternal(0)
  }

  const fetchNextVoicemailPage = () => {
    if (!rootVoicemailViewModelState.voicemailListState?.hasNextPage) return

    mutators.voicemailViewModelState = {
      ...rootVoicemailViewModelState,
      voicemailListFetching: true,
    }
    getVoicemailsInternal(
      rootVoicemailViewModelState.voicemailListState?.nextPageOffset ?? 0,
    )
  }

  const { refetch: fetchVoicemailExtensions } = useGetVoicemailExtensions({
    pbxId: pbxId ?? 0,
    options: {
      enabled: false,
      refetchOnWindowFocus: false,
      refetchInterval: VOICEMAIL_EXTENSIONS_CACHE_STALE_TIME,
      staleTime: VOICEMAIL_EXTENSIONS_CACHE_STALE_TIME,
      gcTime: VOICEMAIL_EXTENSIONS_CACHE_STALE_TIME,
      retry: true,
    },
    onRequestCompleted: (data) => {
      mutators.voicemailExtensionsState = data.results
    },
  })

  const { refetch: fetchAllExtensions } = useGetAllExtensions({
    pbxId: pbxId ?? 0,
    options: {
      enabled: false,
      refetchOnWindowFocus: false,
      staleTime: VOICEMAIL_EXTENSIONS_CACHE_STALE_TIME,
      gcTime: VOICEMAIL_EXTENSIONS_CACHE_STALE_TIME,
      refetchInterval: VOICEMAIL_EXTENSIONS_CACHE_STALE_TIME,
    },
    onRequestCompleted: (data) => {
      setAllExtensionsState(data.results)
    },
  })

  const { mutate: deleteVoicemail } = useDeleteVoicemail({
    onRequestCompleted: ({ voicemailId }) => {
      emit(VoicemailEmitterEventType.VOICEMAIL_DELETED, { voicemailId })
      mutators.voicemailListState = {
        ...rootVoicemailViewModelState.voicemailListState,
        voicemails:
          rootVoicemailViewModelState.voicemailListState?.voicemails?.filter(
            (voicemail) => voicemail.id !== voicemailId,
          ),
      }
    },
  })

  const { mutate: markVoicemailRead } = useMarkVoicemailRead({
    onRequestCompleted: ({ voicemailId }) => {
      emit(VoicemailEmitterEventType.VOICEMAIL_MARKED_READ, { voicemailId })
      updateLocalUnreadStatus(voicemailId, true)
    },
  })

  const { mutate: markVoicemailUnread } = useMarkVoicemailUnread({
    onRequestCompleted: ({ voicemailId }) => {
      emit(VoicemailEmitterEventType.VOICEMAIL_MARKED_UNREAD, { voicemailId })
      updateLocalUnreadStatus(voicemailId, false)
    },
  })

  /**
   * @description Resets all state corresponding to selections while on the VoicemailPage - does not reset extension state or unread counts
   */

  const resetAllSelectionState = () => {
    mutators.voicemailViewModelState = {
      searchValue: SearchValue.empty,
    }
  }

  const appendVoicemailViewModelState = (
    appendingState: VoicemailViewModel,
  ) => {
    mutators.voicemailViewModelState = {
      ...rootVoicemailViewModelState,
      ...appendingState,
    }
  }
  //#endregion
  return {
    voicemailViewModel: {
      ...voicemailViewModel,
      selectedVoicemailLoading,
      selectedVoicemailFetching,
      allExtensionsState,
    } as VoicemailViewModel,
    mutators,
    actions: {
      fetchVoicemailExtensions,
      fetchVoicemailsFromOrigin,
      fetchNextVoicemailPage,
      deleteVoicemail,
      markVoicemailRead,
      markVoicemailUnread,
      resetAllSelectionState,
      appendVoicemailViewModelState,
      moveVoicemailAsync,
      markVoicemailUnreadAsync,
      updateLocalUnreadStatus,
      updateVoicemailExtensionId,
      fetchAllExtensions,
    },
  }
}
